import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { realTimeService } from 'src/app/services/services/realtime.service';
import { GetRequestResponse } from 'src/app/app-models/realTimeModels';

type DialogData = { requestId: number };

@Component({
    selector: 'preview-request-dialog',
    templateUrl: './preview-request-dialog.component.html',
    styleUrls: ['./preview-request-dialog.component.scss'],
    standalone: false
})
export class PreviewRequestDialogComponent implements OnInit {
  loading = false;
  errorKey?: string;

  // view flags
  titleKey = 'realtime:preview';
  isSocial = false;
  isEmail = false;
  isTask = false;

  // social
  socialTo = '';
  socialFrom = '';
  socialMessage = '';

  // email
  emailTo = '';
  emailFrom = '';
  emailReceivedTime = '';
  emailSubject = '';
  emailLink = '';

  // task
  createdBy = '';
  contactName = '';
  phone = '';
  contactId = '';
  duration = '';
  queueKey = '';
  taskSubject = '';
  taskLink = '';

  // transfer (already formatted server-side with entryTimeString)
  transferInfo: { name: string; entryTimeString: string; message: string }[] = [];

  constructor(
    public dialogRef: MatDialogRef<PreviewRequestDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private realTimeService: realTimeService
  ) {}

  ngOnInit(): void {
    const id = Number(this.data?.requestId);
    if (!id) {
      this.errorKey = 'shared_err_genericServerError';
      return;
    }
    this.fetch(id);
  }

  private fetch(requestId: number) {
    this.loading = true;
    this.errorKey = undefined;

    this.realTimeService
      .GetRequest(requestId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(
        (res: GetRequestResponse) => {
          const req = res?.request;
          this.transferInfo = res?.transferInfo ?? [];

          if (req?.additionalInfo) {
            this.parseAdditionalInfo(req.additionalInfo, req.mediaType, req.mediaSubtypeId);
          } else {
            this.titleKey = 'realtime:preview';
          }
        },
        _err => {
          this.errorKey = 'shared_err_genericServerError';
        }
      );
  }

  private parseAdditionalInfo(extInfo: string, mediaType?: number, mediaSubtypeId?: number) {
    // Try JSON first (scheduled task payload)
    const maybeJson = this.tryParseJson(extInfo);
    if (maybeJson && (mediaSubtypeId === 1 || maybeJson.createdBy || maybeJson.duration)) {
      this.isTask = true;
      this.titleKey = 'realtime:queuedetails_lang_scheduledtask';

      this.createdBy   = maybeJson.createdBy || '';
      this.contactName = maybeJson.name || '';
      this.phone       = maybeJson.phone || '';
      this.contactId   = maybeJson.contactId || '';
      this.duration    = (maybeJson.duration || '').toString().replace(/^PT/, '');
      this.queueKey    = maybeJson.queueKey || '';
      this.taskSubject = maybeJson.title || '';
      this.taskLink    = maybeJson.uri || '';
      return;
    }

    // Otherwise treat as XML-ish for social/email
    const pick = (tag: string) => {
      const m = new RegExp(`<${tag}>([\\s\\S]*?)<\\/${tag}>`, 'i').exec(extInfo || '');
      return m ? (m[1] || '').trim() : '';
    };
    const typeMatch = /type=["']?(facebook|twitter|email)["']?/i.exec(extInfo || '');
    const type = (typeMatch?.[1] || '').toLowerCase();

    if (type === 'facebook' || type === 'twitter') {
      this.isSocial = true;
      this.titleKey = `realtime:${type}`;
      this.socialMessage = pick('Message');
      this.socialFrom    = pick('Author');
      this.socialTo      = pick('NetworkStreamName');
      return;
    }

    // Email fallback (mediaType == email in old app) or presence of these tags
    const from     = pick('from');
    const to       = pick('to');
    const received = pick('receivedtime');
    const subject  = pick('subject');
    const uri      = pick('uri');

    if (from || to || subject) {
      this.isEmail = true;
      this.titleKey = 'realtime:email';
      this.emailFrom         = from;
      this.emailTo           = to;
      this.emailReceivedTime = received;
      this.emailSubject      = subject;
      this.emailLink         = uri;
      return;
    }

    this.titleKey = 'realtime:preview';
  }

  private tryParseJson(s: string): any | null {
    try { return JSON.parse(s); } catch { return null; }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
